import { useEffect, useRef } from "react";

import { DEBUG_WALLET, useWeb3User, useUserTokens } from "@bridgesplit/react";
import { Result, getReadableErrorMessage } from "@bridgesplit/utils";
import { useWallet } from "@solana/wallet-adapter-react";
import { useDispatch } from "react-redux";

import { updateWallet, useAbfFetches, useAuthStorage } from "../reducers";
import { TransactionWalletAuth, AbfUserWithPermissionsAndWallets } from "../types";
import { useActiveWallet } from "./wallet";
import { createDefaultUser } from "./utils";
import { useUserMeParams } from "./points";
interface UseUserResponse {
    data: AbfUserWithPermissionsAndWallets | undefined;
    skip?: boolean;
    isError?: boolean;
    isLoading: boolean;
    isFetching?: boolean;
    error?: string;
}
export function useUser(options?: { skip?: boolean }): UseUserResponse {
    const { params, skip, isLoading } = useUserMeParams();

    if (isLoading) {
        return {
            data: undefined,
            skip: skip,
            isError: true,
            isLoading: true,
            isFetching: true
        };
    }

    if (!params?.walletPubkey) {
        return {
            data: undefined,
            skip: skip,
            isLoading: false,
            isError: true,
            isFetching: false
        };
    }

    const user = createDefaultUser(params?.walletPubkey);

    return {
        data: user,
        skip: options?.skip || skip,
        isLoading: false,
        isError: false,
        isFetching: false
    };
}

export function useSkipUnauthenticated() {
    const { activeWallet } = useActiveWallet();
    return !activeWallet;
}

export function useTransactionWalletAuth() {
    const { activeWallet } = useActiveWallet();
    const { getWeb3UserResult } = useWeb3User();

    return function (): Result<TransactionWalletAuth> {
        try {
            if (!activeWallet) return Result.errFromMessage("You haven't registered your wallet");
            const web3User = getWeb3UserResult();
            if (!web3User.isOk()) return Result.err(web3User);

            return Result.ok({ wallet: web3User.unwrap().wallet });
        } catch (error) {
            // catches browser wallet errors
            return Result.errWithDebug(getReadableErrorMessage("access your connected wallet"), error);
        }
    };
}
export function useAbfLogout() {
    const { disconnect, select } = useWallet();
    const { resetCookies } = useAuthStorage();

    function logOut() {
        resetCookies();
        disconnect();
        select(null);
    }

    return { logOut };
}

/**
 * Hook that detects wallet changes and invalidates relevant API caches
 *
 * When a user connects a different wallet, this hook ensures that all cached data
 * is invalidated and refreshed to reflect the new wallet's data. It tracks the
 * previous wallet address and compares it with the current one to detect changes.
 * When a change is detected, it resets various API caches related to user assets,
 * lending positions, loops, and rewards.
 */
export function useInvalidateOnWalletChange() {
    const { wallet } = useWallet();
    const dispatch = useDispatch();
    const { resetLendingVaultPositionsApi, resetLoopApi, resetRewardsApi, resetPointsAndReferrals } = useAbfFetches();
    const { refresh } = useUserTokens();
    const previousWallet = useRef<string | null>(null);
    const walletPubkey = wallet?.adapter?.publicKey?.toString() ?? null;

    useEffect(() => {
        if (walletPubkey !== previousWallet.current) {
            previousWallet.current = walletPubkey;
            void refresh();
            resetLoopApi();
            resetRewardsApi();
            resetLendingVaultPositionsApi();
            resetPointsAndReferrals();
        }
    }, [
        walletPubkey,
        dispatch,
        resetLendingVaultPositionsApi,
        resetLoopApi,
        resetRewardsApi,
        resetPointsAndReferrals,
        refresh
    ]);
}

/**
 * Hook that synchronizes the wallet address between the wallet adapter and the auth store
 *
 * This hook ensures that the wallet address stored in the application's auth state
 * is always in sync with the actual connected wallet. When it detects a mismatch
 * between the stored wallet address and the current wallet's public key, it
 * dispatches an action to update the auth store with the current wallet address.
 * This synchronization is crucial for maintaining consistent authentication state
 * across the application.
 */
export function useSyncWalletStore() {
    const { wallet } = useWallet();
    const walletPubkey = wallet?.adapter?.publicKey?.toString() ?? null;
    const dispatch = useDispatch();
    const { state } = useAuthStorage();

    // If DEBUG_WALLET is set, use it instead of the connected wallet
    const effectiveWalletPubkey = DEBUG_WALLET || walletPubkey;

    useEffect(() => {
        if (state.wallet !== effectiveWalletPubkey) {
            dispatch(updateWallet(effectiveWalletPubkey));
        }
    }, [effectiveWalletPubkey, state.wallet, dispatch, walletPubkey]);
}
